import React from 'react';
import injectSheet from 'react-jss/lib/injectSheet';
import PropTypes from 'prop-types';
import cn from 'classnames';
import StarIcon from '../../../../icons/StarIcon';
import theme from '../../../../styles/theme';
import StarFillIcon from '../../../../icons/StarFillIcon';
import Tooltip from '../../../Tooltip';
import { TooltipPlacement } from '../../../Tooltip/constants';
import { aliceLikeButtonHoverStyle, aliceLikeButtonStyle } from '../../styleButton';

const FavouriteButton = ({
  classes,
  isFavourited,
  isLoading,
  isAuthenticated,
  onClick,
  onTrackClick,
}) => {
  const buttonLabel = (!isAuthenticated || !isFavourited) ? 'Favourite' : 'Favourited';
  const FavouriteIcon = isFavourited ? StarFillIcon : StarIcon;

  const button = (
    <button
      type="button"
      className={cn(classes.button, classes.buttonHover)}
      disabled={isLoading}
      onClick={() => {
        if (onTrackClick) onTrackClick();
        if (isAuthenticated && onClick) {
          onClick();
        }
      }}
    >
      <p>{buttonLabel}</p>
      {isLoading
        ? <div className={cn(classes.spinner)} data-testid="favourite-button-spinner" />
        : <FavouriteIcon />}
    </button>
  );

  if (!isAuthenticated) {
    return (
      <Tooltip
        placement={TooltipPlacement.BOTTOM_START}
        content={(
          <>
            <p className={classes.tooltipTitle}>Follow your favourite brands</p>
            <p>For updates on new arrivals, sale items & more.</p>
            <p>Log in to shop your way.</p>
            <div className={classes.buttonsWrapper}>
              <a href="/customer/account/login?initial_screen=register" className={cn(classes.tooltipButton)}>
                Create new account
              </a>
              <a href="/customer/account/login" className={cn(classes.tooltipButton, classes.tooltipButtonLogin)}>
                Log in
              </a>
            </div>
          </>
        )}
        tooltipClass={classes.tooltip}
        showCloseButton
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};

FavouriteButton.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isFavourited: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func,
  onTrackClick: PropTypes.func,
  isAuthenticated: PropTypes.bool,
};

FavouriteButton.defaultProps = {
  isFavourited: false,
  isLoading: false,
  isAuthenticated: false,
  onClick: null,
  onTrackClick: null,
};

/**
 * @type {Object.<string, React.CSSProperties | Object.<string, React.CSSProperties>>}
 */
const styles = {
  button: aliceLikeButtonStyle,
  buttonHover: aliceLikeButtonHoverStyle,
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  spinner: {
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #6f6e6eff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    width: 14,
    height: 14,
  },
  tooltip: {
    backgroundColor: theme.colors.white,
    color: theme.colors.black,
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.30))',
    borderRadius: 4,
    lineHeight: 1.5,
    maxWidth: 272,

    '& p': {
      margin: 0,
      letterSpacing: '0.3px',
      fontWeight: theme.fonts.lightWeight,
      fontSize: 14,
      marginBottom: 8,
      fontFamily: theme.fonts.light,
    },

    '& svg': {
      color: theme.colors.black,
    },
  },
  tooltipTitle: {
    '&&': {
      fontFamily: theme.fonts.bold,
      fontWeight: theme.fonts.boldWeight,
    },
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '4px',
  },
  tooltipButton: {
    '&&': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      border: 'none',
      backgroundColor: theme.colors.white,
      textTransform: 'uppercase',
      fontSize: 11,
      fontFamily: theme.fonts.body,
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      color: theme.colors.black,
    },

    '&&:hover': {
      backgroundColor: theme.colors.secondary,
      borderColor: theme.colors.secondary,
      color: theme.colors.white,
      transition: 'background-color 0.25s ease-out, color 0.25s ease-out, border 0.25s ease-out',
    },
  },
  tooltipButtonLogin: {
    '&&': {
      color: 'rgb(66, 171, 200)',
    },
  },
};

export default injectSheet(styles)(FavouriteButton);
